// DayStrip (timesheet rules §3): the quick edit's day, on the board's own timeline. The hours on top
// (TimeScale, alone); Booked: the board's visits at their booked windows (VisitBlock, compact); Clocked:
// what the iPad recorded (TimeBar on TimeTrack), the problem in red, and what Save would write dashed
// over it; NowLine today; a dashed line at midnight when the day runs past it; a one-line key. Where the
// two rows differ is the useful part. A locked day draws it still. Narrow (a phone), each row's word
// goes above its row, so the hours have the whole width and TimeScale thins only what still won't fit.
// Reads only --dstrip-*.
import { NowLine } from '../../components/NowLine/NowLine.tsx';
import { TimeBar } from '../../components/TimeBar/TimeBar.tsx';
import { TimeScale } from '../../components/TimeScale/TimeScale.tsx';
import { TimeTrack, TrackMark } from '../../components/TimeTrack/TimeTrack.tsx';
import { TimeSpanProvider } from '../../components/time-span.tsx';
import { VisitBlock } from '../../components/VisitBlock/VisitBlock.tsx';
import type { QuickEditView } from '../timesheet-view.ts';
import './DayStrip.css';

export interface DayStripProps {
  strip: QuickEditView['strip'];
  /** A part pressed (not on a locked day). */
  onPart?: (id: string) => void;
  density?: 'comfortable' | 'compact';
}

export const STRIP_KEY =
  'Hatched: done · Solid: running now · Red dashed: no clock-out · Dashed ink: not saved yet';

export function DayStrip({ strip, onPart, density = 'comfortable' }: DayStripProps) {
  const { booked, clocked, ghosts, nowMinute, midnight, pressable } = strip;
  const ends = [
    ...booked.flatMap((b) => [b.startMinute, b.endMinute]),
    ...[...clocked, ...ghosts].flatMap((p) => [p.startMinute, p.endMinute ?? p.startMinute + 30]),
    ...(nowMinute !== null ? [nowMinute] : []),
  ];
  const from = Math.min(7 * 60, ...ends);
  const to = Math.max(18 * 60, ...ends.map((m) => m + 30));
  const parts = [
    ...clocked.map((p) => ({ ...p, start: p.startMinute, end: p.endMinute })),
    ...ghosts.map((g) => ({ ...g, id: `ghost:${g.id}`, start: g.startMinute, end: g.endMinute })),
  ];
  return (
    <TimeSpanProvider from={from} to={to} now={nowMinute} density={density} readOnly={!pressable}>
      <div className="fl-dstrip">
        <div className="fl-dstrip__grid">
          <span className="fl-dstrip__corner" aria-hidden="true" />
          <TimeScale alone />
          <span className="fl-dstrip__label" aria-hidden="true">
            Booked
          </span>
          <TimeTrack
            label="Booked"
            items={booked.map((b) => ({ ...b, start: b.startMinute, end: b.endMinute }))}
            renderItem={(b, at) => (
              <span
                className="fl-dstrip__slot"
                style={{ insetInlineStart: `${at.from * 100}%`, inlineSize: `${(at.to - at.from) * 100}%` }}
              >
                <VisitBlock
                  state={b.drawn}
                  time={b.time ?? ''}
                  timeShort={b.time ?? ''}
                  customer={b.label}
                  job={b.job ?? ''}
                  density="compact"
                  label={`Booked: ${b.label}${b.time ? `, ${b.time}` : ''}${b.arrival ? `, arrival window ${b.arrival}` : ''}`}
                  tabIndex={-1}
                />
              </span>
            )}
          >
            <NowLine />
          </TimeTrack>
          <span className="fl-dstrip__label" aria-hidden="true">
            Clocked
          </span>
          <TimeTrack
            label="Clocked"
            stack={false}
            items={parts}
            renderItem={(p, at) => (
              <TimeBar
                kind={p.kind}
                from={at.from}
                to={at.to}
                label={p.label}
                name={p.name}
                pen={p.pen ?? null}
                state={p.state}
                toCheck={p.toCheck}
                paid={p.paid}
                isSelected={p.isSelected}
                isGhost={p.isGhost}
                {...(pressable && onPart && !p.isGhost ? { onPress: () => onPart(p.id) } : {})}
              />
            )}
          >
            <NowLine />
            {midnight ? <TrackMark minute={1440} label={midnight} /> : null}
          </TimeTrack>
          <p className="fl-dstrip__key">{STRIP_KEY}</p>
        </div>
      </div>
    </TimeSpanProvider>
  );
}
